/**
 * Phép tính thuần của phân hệ License on-premise (ADR-0058).
 *
 * Bản Java ở license-service là chốt chặn thật; bản này để giao diện báo
 * **ngay lúc soạn** (ô lệch gói, cần phê duyệt hay không, cửa sổ gia hạn) mà
 * không chờ round-trip, và để repository mock ra cùng đáp án với backend.
 *
 * Luật của thư mục giống `goi-dich-vu/nghiep-vu`: không đọc đồng hồ (nhận
 * `bayGio` làm tham số), không gọi HTTP, không dựng chuỗi hiển thị — trả mã để
 * view tự dịch.
 *
 * Mốc thời gian của license là `timestamptz` (khác `Ngay` của gói dịch vụ), nên
 * ở đây làm việc bằng mili giây và ghi ra ISO kèm `+07:00`: `ngayVN()` cắt 10
 * ký tự đầu, ghi ra `Z` thì một license hết hạn lúc 00:00 giờ Việt Nam hiện
 * lùi một ngày.
 */
import type {
  ChinhSachLicense,
  Entitlements,
  KhoaTrongKeyring,
  KieuYeuCau,
  License,
  LyDoDuyet,
  TrangThaiLicense,
  TrangThaiYeuCau,
  YeuCauKichHoat,
  YeuCauLicense,
} from './types.ts';

export const MOT_NGAY_MS = 86_400_000;

const LECH_VN_MS = 7 * 3_600_000;

/** Giá trị mặc định của các tham số `license.*` (hợp đồng triển khai mục 3). */
export const CHINH_SACH_MAC_DINH: ChinhSachLicense = {
  default_term_months: 12,
  grace_days: 7,
  renewal_window_days: 45,
  approval_max_term_months: 12,
  approval_over_plan_quota: true,
  approval_special_features: ['*'],
};

/** `computed_status = EXPIRING` khi còn từ ngần này ngày trở xuống (trạng thái cục bộ, hợp đồng mục 7). */
export const NGUONG_SAP_HET_HAN_NGAY = 30;

/** Bộ lọc "sắp hết hạn" của màn License — trùng cửa sổ gia hạn mặc định. */
export const LOC_SAP_HET_HAN_NGAY = 45;

const ms = (iso: string): number => new Date(iso).getTime();

/** Mốc thời gian → ISO theo giờ Việt Nam: `2026-11-15T00:00:00+07:00`. */
export function isoVN(moc: number): string {
  return `${new Date(moc + LECH_VN_MS).toISOString().slice(0, 19)}+07:00`;
}

/**
 * Cộng tháng theo lịch Việt Nam, kẹp ngày cuối tháng: 31/01 + 1 tháng = 28/02
 * (hoặc 29/02), không tràn sang tháng Ba như `Date.setMonth`.
 */
export function congThang(iso: string, soThang: number): string {
  const d = new Date(ms(iso) + LECH_VN_MS);
  const nam = d.getUTCFullYear();
  const thang = d.getUTCMonth() + soThang;
  const cuoiThang = new Date(Date.UTC(nam, thang + 1, 0)).getUTCDate();
  const ngay = Math.min(d.getUTCDate(), cuoiThang);
  const moi = Date.UTC(
    nam,
    thang,
    ngay,
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  );
  return isoVN(moi - LECH_VN_MS);
}

export function congNgay(iso: string, soNgay: number): string {
  return isoVN(ms(iso) + soNgay * MOT_NGAY_MS);
}

/** Số ngày còn tới `den`, làm tròn lên: còn 1 giờ vẫn là "còn 1 ngày". Âm khi đã qua. */
export function soNgayToi(den: string, bayGio: Date): number {
  return Math.ceil((ms(den) - bayGio.getTime()) / MOT_NGAY_MS);
}

/** `grace_until = expires_at + license.grace_days` (ADR-0058 mục 4 bước 3). */
export function tinhAnHan(expiresAt: string, chinhSach: ChinhSachLicense = CHINH_SACH_MAC_DINH): string {
  return congNgay(expiresAt, chinhSach.grace_days);
}

type LicenseTho = Omit<License, 'computed_status' | 'days_left'>;

/**
 * `computed_status` và `days_left` của một license.
 *
 * Thứ tự xét có nghĩa: thu hồi thắng mọi thứ; bị thay thế chỉ khi bản kế
 * nhiệm **đã có hiệu lực** và chưa bị thu hồi — bản gia hạn đặt `not_before`
 * bằng `expires_at` cũ, nên trước mốc đó bản cũ vẫn là bản đang chạy.
 */
export function tinhTrangThai(
  l: LicenseTho,
  bayGio: Date,
  cacLicense: readonly LicenseTho[] = [],
): { computed_status: TrangThaiLicense; days_left: number } {
  const days_left = soNgayToi(l.expires_at, bayGio);
  const t = bayGio.getTime();

  if (l.revoked_at) return { computed_status: 'REVOKED', days_left };

  const biThay = cacLicense.some(
    (k) => k.supersedes_id === l.license_id && !k.revoked_at && ms(k.not_before) <= t,
  );
  if (biThay) return { computed_status: 'SUPERSEDED', days_left };

  if (t < ms(l.not_before)) return { computed_status: 'NOT_YET_VALID', days_left };
  if (t > ms(l.grace_until)) return { computed_status: 'EXPIRED', days_left };
  if (t > ms(l.expires_at)) return { computed_status: 'GRACE', days_left };
  if (days_left <= NGUONG_SAP_HET_HAN_NGAY) return { computed_status: 'EXPIRING', days_left };
  return { computed_status: 'ACTIVE', days_left };
}

/** Trạng thái còn đang cho máy khách chạy. */
const DANG_CHAY: readonly TrangThaiLicense[] = ['ACTIVE', 'EXPIRING', 'GRACE'];

/**
 * License đang quyết định quyền dùng của tenant: trong các bản còn chạy, bản có
 * `expires_at` muộn nhất (DT21 14.6 — runtime chọn đúng như vậy).
 */
export function licenseHieuLuc(cacLicense: readonly License[]): License | null {
  let chon: License | null = null;
  for (const l of cacLicense) {
    if (!DANG_CHAY.includes(l.computed_status)) continue;
    if (!chon || ms(l.expires_at) > ms(chon.expires_at)) chon = l;
  }
  return chon;
}

/**
 * Nút "Gia hạn" hiện khi license còn chạy và còn tới hạn trong
 * `license.renewal_window_days` ngày (mặc định 45). Đang ân hạn cũng gia hạn
 * được — đó là lúc cần nhất.
 */
export function trongCuaSoGiaHan(
  l: Pick<License, 'computed_status' | 'days_left'>,
  chinhSach: ChinhSachLicense = CHINH_SACH_MAC_DINH,
): boolean {
  return DANG_CHAY.includes(l.computed_status) && l.days_left <= chinhSach.renewal_window_days;
}

/** Bộ lọc "Sắp hết hạn trong 45 ngày": còn chạy, chưa quá `expires_at`. */
export function locSapHetHan<T extends Pick<License, 'computed_status' | 'days_left'>>(
  cacLicense: readonly T[],
  soNgay: number = LOC_SAP_HET_HAN_NGAY,
): T[] {
  return cacLicense.filter(
    (l) =>
      (l.computed_status === 'ACTIVE' || l.computed_status === 'EXPIRING') &&
      l.days_left >= 0 &&
      l.days_left <= soNgay,
  );
}

export function locAnHan<T extends Pick<License, 'computed_status'>>(cacLicense: readonly T[]): T[] {
  return cacLicense.filter((l) => l.computed_status === 'GRACE');
}

/**
 * Dòng thời gian: mỗi chuỗi gia hạn đi từ bản mới nhất lùi về bản gốc theo
 * `supersedes_id`; chuỗi mới hơn đứng trước.
 */
export function xepDongThoiGian<T extends Pick<License, 'license_id' | 'supersedes_id' | 'issued_at'>>(
  cacLicense: readonly T[],
): T[] {
  const theoId = new Map(cacLicense.map((l) => [l.license_id, l]));
  const coKeNhiem = new Set(cacLicense.map((l) => l.supersedes_id).filter(Boolean));
  const dau = cacLicense
    .filter((l) => !coKeNhiem.has(l.license_id))
    .sort((a, b) => ms(b.issued_at) - ms(a.issued_at));

  const ra: T[] = [];
  const daXep = new Set<string>();
  for (const d of dau) {
    let hienTai: T | undefined = d;
    while (hienTai && !daXep.has(hienTai.license_id)) {
      ra.push(hienTai);
      daXep.add(hienTai.license_id);
      hienTai = hienTai.supersedes_id ? theoId.get(hienTai.supersedes_id) : undefined;
    }
  }
  // Vòng lặp dữ liệu hỏng (A thay B, B thay A) không có đầu chuỗi: vẫn hiện, xếp theo ngày.
  for (const l of [...cacLicense].sort((a, b) => ms(b.issued_at) - ms(a.issued_at))) {
    if (!daXep.has(l.license_id)) ra.push(l);
  }
  return ra;
}

/**
 * Loại yêu cầu khi cấp mới: tenant đã có license chạy trên **cài đặt khác** thì
 * đây là thay thế (`REPLACE`, DT21 14.6), không phải cấp thêm.
 */
export function chonKieuYeuCau(
  installationId: string,
  hieuLuc: Pick<License, 'installation_id'> | null,
): KieuYeuCau {
  return hieuLuc && hieuLuc.installation_id !== installationId ? 'REPLACE' : 'ISSUE';
}

// ---------------------------------------------------------------------------
// Nội dung dựng sẵn từ gói
// ---------------------------------------------------------------------------

/** Hạn mức của một `plan_code` (`plan_quota`). `null` ở một ô là buộc nhập theo hợp đồng. */
export interface HanMucGoi {
  max_users: number | null;
  max_nodes: number | null;
  limits: Record<string, number | null>;
}

/** Dạng tối thiểu của một dịch vụ mà phép dựng cần — khớp `DichVu` của ADR-0019. */
export interface DichVuToiThieu {
  ma: string;
  bat_buoc: boolean;
  vung_api: readonly string[];
}

/**
 * Dựng `entitlements` từ gói (ADR-0058 mục 4 bước 3):
 * module là **hợp** các `vung_api` của dịch vụ trong gói cộng dịch vụ bắt buộc;
 * giới hạn chép từ `plan_quota`; `features` rỗng — tính năng đặc biệt luôn là
 * phần khác hợp đồng, người cấp tự thêm.
 */
export function dungNoiDungTuGoi(
  dichVuCuaGoi: readonly string[],
  danhMuc: readonly DichVuToiThieu[],
  hanMuc: HanMucGoi,
): Entitlements {
  const trongGoi = new Set(dichVuCuaGoi);
  const modules = new Set<string>();
  for (const dv of danhMuc) {
    if (dv.bat_buoc || trongGoi.has(dv.ma)) dv.vung_api.forEach((m) => modules.add(m));
  }
  return {
    modules: [...modules].sort(),
    max_users: hanMuc.max_users,
    max_nodes: hanMuc.max_nodes,
    limits: { ...hanMuc.limits },
    features: [],
  };
}

/** Khoá ô của nội dung: `modules`, `max_users`, `max_nodes`, `features`, `limits.<tên>`. */
export type OLicense = string;

const cungTap = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

/** Những ô của nội dung đang khác bản dựng sẵn từ gói — giao diện đánh dấu chúng. */
export function oLechGoi(noiDung: Entitlements, mau: Entitlements): OLicense[] {
  const ra: OLicense[] = [];
  if (!cungTap(noiDung.modules, mau.modules)) ra.push('modules');
  if (noiDung.max_users !== mau.max_users) ra.push('max_users');
  if (noiDung.max_nodes !== mau.max_nodes) ra.push('max_nodes');
  const tenGioiHan = new Set([...Object.keys(mau.limits), ...Object.keys(noiDung.limits)]);
  for (const ten of [...tenGioiHan].sort()) {
    if ((noiDung.limits[ten] ?? null) !== (mau.limits[ten] ?? null)) ra.push(`limits.${ten}`);
  }
  if (!cungTap(noiDung.features, mau.features)) ra.push('features');
  return ra;
}

/** Ô bắt buộc mà còn trống — Enterprise để trống giới hạn để buộc nhập theo hợp đồng. */
export function oConTrong(noiDung: Entitlements): OLicense[] {
  const ra: OLicense[] = [];
  if (noiDung.max_users === null) ra.push('max_users');
  if (noiDung.max_nodes === null) ra.push('max_nodes');
  for (const [ten, gt] of Object.entries(noiDung.limits)) {
    if (gt === null) ra.push(`limits.${ten}`);
  }
  return ra;
}

const vuot = (gt: number | null, tran: number | null | undefined): boolean =>
  gt !== null && tran !== null && tran !== undefined && gt > tran;

export interface ThamSoDuyet {
  kind: KieuYeuCau;
  noiDung: Entitlements;
  /** Hạn mức và module của gói — mốc để xét "vượt gói". */
  goc: Entitlements;
  not_before: string;
  expires_at: string;
  /** `expires_at` của license cũ, chỉ có khi `kind = RENEW`. */
  hetHanCu?: string | null;
  bayGio: Date;
  chinhSach?: ChinhSachLicense;
}

/**
 * `needs_approval` và lý do (ADR-0058 mục 4 bước 4). Mỗi lý do ứng với một
 * tham số; tham số chỉ được **siết**, nên phép tính không bao giờ nới quá
 * mức DT21 (12 tháng).
 */
export function tinhCanDuyet(p: ThamSoDuyet): { needs_approval: boolean; approval_reasons: LyDoDuyet[] } {
  const cs = p.chinhSach ?? CHINH_SACH_MAC_DINH;
  const lyDo: LyDoDuyet[] = [];

  const tranThang = Math.min(cs.approval_max_term_months, 12);
  if (ms(p.expires_at) > ms(congThang(p.not_before, tranThang))) lyDo.push('TERM_OVER_MAX');

  const dacBiet = cs.approval_special_features;
  if (p.noiDung.features.some((f) => dacBiet.includes('*') || dacBiet.includes(f))) {
    lyDo.push('SPECIAL_FEATURES');
  }

  if (cs.approval_over_plan_quota) {
    const ngoaiGoi = p.noiDung.modules.some((m) => !p.goc.modules.includes(m));
    const vuotGioiHan =
      vuot(p.noiDung.max_users, p.goc.max_users) ||
      vuot(p.noiDung.max_nodes, p.goc.max_nodes) ||
      Object.entries(p.noiDung.limits).some(([ten, gt]) => vuot(gt, p.goc.limits[ten]));
    if (ngoaiGoi || vuotGioiHan) lyDo.push('OVER_PLAN_QUOTA');
  }

  if (
    p.kind === 'RENEW' &&
    p.hetHanCu &&
    ms(p.hetHanCu) - p.bayGio.getTime() > cs.renewal_window_days * MOT_NGAY_MS
  ) {
    lyDo.push('RENEW_BEFORE_WINDOW');
  }

  return { needs_approval: lyDo.length > 0, approval_reasons: lyDo };
}

// ---------------------------------------------------------------------------
// Huỷ yêu cầu
// ---------------------------------------------------------------------------

/** Trạng thái còn huỷ được — khớp `LicenseNghiepVu.huy` của license-service. */
export const HUY_DUOC: readonly TrangThaiYeuCau[] = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'];

/**
 * Có hiện mục "Huỷ yêu cầu" không: yêu cầu còn ở trạng thái huỷ được, và người
 * xem là người tạo hoặc có quyền cấp. Backend vẫn kiểm độc lập; đây là UX.
 */
export function coTheHuyYeuCau(
  yeuCau: Pick<YeuCauLicense, 'status' | 'created_by'>,
  nguoiXem: string | undefined,
  duocCap: boolean,
): boolean {
  return HUY_DUOC.includes(yeuCau.status) && (duocCap || (nguoiXem !== undefined && yeuCau.created_by === nguoiXem));
}

// ---------------------------------------------------------------------------
// activation-request.json
// ---------------------------------------------------------------------------

/**
 * Chuẩn hoá JSON theo JCS (RFC 8785): khoá sắp theo đơn vị UTF-16, không
 * khoảng trắng, số theo cách tuần tự hoá của ECMAScript — chính là
 * `JSON.stringify` cho số hữu hạn. Server chuẩn hoá cùng cách rồi tính
 * `request_digest`, nên digest hiện ở trình duyệt khớp digest server lưu.
 */
export function chuanHoaJson(gt: unknown): string {
  if (gt === null || typeof gt === 'boolean' || typeof gt === 'string') return JSON.stringify(gt);
  if (typeof gt === 'number') {
    if (!Number.isFinite(gt)) throw new RangeError('JCS không biểu diễn được NaN hay Infinity');
    return JSON.stringify(gt);
  }
  if (Array.isArray(gt)) return `[${gt.map(chuanHoaJson).join(',')}]`;
  if (typeof gt === 'object') {
    const khoa = Object.keys(gt as object)
      .filter((k) => (gt as Record<string, unknown>)[k] !== undefined)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${khoa
      .map((k) => `${JSON.stringify(k)}:${chuanHoaJson((gt as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  }
  throw new TypeError(`JCS không biểu diễn được kiểu ${typeof gt}`);
}

/** `hex(SHA-256(chuỗi))` bằng Web Crypto — có ở trình duyệt và Node 20+. */
export async function digestSha256(chuoi: string): Promise<string> {
  const bam = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(chuoi));
  return [...new Uint8Array(bam)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type LoiKichHoat =
  | { ma: 'JSON_HONG' }
  | { ma: 'KHONG_PHAI_DOI_TUONG' }
  | { ma: 'THIEU_TRUONG'; truong: string }
  | { ma: 'SAI_UUID'; truong: string };

/**
 * Đọc `activation-request.json` ở trình duyệt. Chỉ kiểm hình dạng; khớp tenant
 * và idempotency là việc của server.
 */
export function docYeuCauKichHoat(
  vanBan: string,
): { ok: true; data: YeuCauKichHoat } | { ok: false; loi: LoiKichHoat } {
  let goc: unknown;
  try {
    goc = JSON.parse(vanBan);
  } catch {
    return { ok: false, loi: { ma: 'JSON_HONG' } };
  }
  if (goc === null || typeof goc !== 'object' || Array.isArray(goc)) {
    return { ok: false, loi: { ma: 'KHONG_PHAI_DOI_TUONG' } };
  }
  const o = goc as Record<string, unknown>;
  for (const truong of ['installation_id', 'tenant_id', 'product_code']) {
    if (typeof o[truong] !== 'string' || (o[truong] as string).trim() === '') {
      return { ok: false, loi: { ma: 'THIEU_TRUONG', truong } };
    }
  }
  // Chỉ `installation_id` buộc là UUID (bộ cài sinh UUIDv4, ADR-0058 mục 4 bước 2).
  // `tenant_id` để server đối chiếu: giao diện không giả định định dạng mã tenant.
  if (!UUID.test(o.installation_id as string)) {
    return { ok: false, loi: { ma: 'SAI_UUID', truong: 'installation_id' } };
  }
  return {
    ok: true,
    data: {
      installation_id: (o.installation_id as string).toLowerCase(),
      tenant_id: (o.tenant_id as string).trim(),
      product_code: o.product_code as string,
      version: typeof o.version === 'string' ? o.version : null,
      created_at: typeof o.created_at === 'string' ? o.created_at : null,
      goc: o,
    },
  };
}

// ---------------------------------------------------------------------------
// JWS công khai: keyring, danh sách thu hồi
// ---------------------------------------------------------------------------

function base64UrlSangChuoi(phan: string): string {
  const b64 = phan.replace(/-/g, '+').replace(/_/g, '/');
  const du = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const nhiPhan = atob(b64 + du);
  return new TextDecoder().decode(Uint8Array.from(nhiPhan, (c) => c.charCodeAt(0)));
}

export function chuoiSangBase64Url(chuoi: string): string {
  const byte = new TextEncoder().encode(chuoi);
  let nhiPhan = '';
  byte.forEach((b) => {
    nhiPhan += String.fromCharCode(b);
  });
  return btoa(nhiPhan).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Giải header và payload của một JWS compact để **hiển thị**. Không kiểm chữ
 * ký: giao diện không phải nơi tin cậy, server đã kiểm khi nhận keyring.
 */
export function giaiMaJws(
  artifact: string,
): { header: Record<string, unknown>; payload: Record<string, unknown> } | null {
  const phan = artifact.trim().split('.');
  if (phan.length !== 3) return null;
  try {
    const header = JSON.parse(base64UrlSangChuoi(phan[0])) as unknown;
    const payload = JSON.parse(base64UrlSangChuoi(phan[1])) as unknown;
    if (!header || typeof header !== 'object' || !payload || typeof payload !== 'object') return null;
    return {
      header: header as Record<string, unknown>,
      payload: payload as Record<string, unknown>,
    };
  } catch {
    return null;
  }
}

/** Lỗi phát hiện được ngay ở trình duyệt khi chọn tệp keyring để tải lên. */
export type LoiKeyring = 'KHONG_PHAI_KEYRING' | 'KHONG_CO_KHOA' | 'SEQUENCE_KHONG_MOI';

export type KeyringTaiLen =
  | { ok: true; artifact: string; sequence: number; kyBoi: string; cacKhoa: KhoaTrongKeyring[] }
  | { ok: false; loi: LoiKeyring };

/**
 * Đọc tệp keyring người duyệt chọn để tải lên (ADR-0058 mục 5, runbook nghi thức
 * khoá gốc bước 9). Chỉ chặn sớm những lỗi đọc được bằng mắt (không phải JWS
 * `typ=KEYRING`, không có khoá, `sequence` không lớn hơn bản hiện hành) để người
 * dùng sửa trước khi gửi; chữ ký bằng khoá gốc và `x` từng `kid` do
 * license-service kiểm.
 *
 * @param sequenceHienHanh `null` khi máy chủ chưa có keyring nào
 */
export function docKeyringTaiLen(noiDung: string, sequenceHienHanh: number | null): KeyringTaiLen {
  const artifact = noiDung.trim();
  const jws = giaiMaJws(artifact);
  if (!jws || jws.header.typ !== 'KEYRING' || typeof jws.payload.sequence !== 'number') {
    return { ok: false, loi: 'KHONG_PHAI_KEYRING' };
  }
  const cacKhoa = Array.isArray(jws.payload.keys) ? (jws.payload.keys as KhoaTrongKeyring[]) : [];
  if (cacKhoa.length === 0) return { ok: false, loi: 'KHONG_CO_KHOA' };
  const sequence = jws.payload.sequence;
  if (sequenceHienHanh !== null && sequence <= sequenceHienHanh) return { ok: false, loi: 'SEQUENCE_KHONG_MOI' };
  return { ok: true, artifact, sequence, kyBoi: typeof jws.header.kid === 'string' ? jws.header.kid : '', cacKhoa };
}
